import { IIndicationCard } from 'components/project/clinic/IndicationCard';
import { antagonistMapper } from 'lib/clinic/mapper';
import { overlappingArrayElements } from 'lib/library';
import { materialImgList } from 'lib/teeth/teethMaterialMapper';
import {
  IIndicationData,
  IIndicationFormat,
  IIndicationGroup,
  IIndicationInfo,
  IMaterialData,
  IMaterialFormat,
  IMaterialInfo,
  IPartInfo,
  ISeqName,
  ITooth,
  IToothShadeInfo,
} from 'lib/types/clinic/projectTypes';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UtilActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { useCreateProjectIndicationStore } from 'store/local/projectCreateindication';
import { useProjectDetailStore } from 'store/local/projectDetail';

export const useCreateProjectIndication = () => {
  // SECTION: declare server state
  const {
    user: { groupCode },
    indicationFormatData,
    indicationInfoData,
    loadedSource,
  } = useShallowAppSelector(state => ({
    user: state.user.user || {},
    indicationFormatData: state.util.indicationFormat.data?.indicationFormat,
    indicationInfoData: state.util.indicationInfo.data?.indicationInfo,
    loadedSource: state.app.loadedSource,
  }));

  // SECTION: declare local state
  const {
    tooth,
    setTooth,
    indicationTab,
    setIndicationTab,
    teeth,
    setTeeth,
    indicationAlias,
    setIndicationAlias,
    materialAlias,
    setMaterialAlias,
    isTypeMore,
    setIsTypeMore,
    setIsHasData,
    reset: createProjectIndicationReset,
  } = useCreateProjectIndicationStore();
  const { isPhotogrammetry } = useProjectDetailStore();

  // SECTION: declare variable
  const { t } = useTranslation();
  const indicationIdx = useMemo(() => {
    return !!tooth?.indicationIdx ? tooth?.indicationIdx : '';
  }, [tooth?.indicationIdx]);
  const materialIdx = useMemo(() => {
    return !!tooth?.material ? tooth?.material : '';
  }, [tooth?.material]);
  const materialShade = useMemo(() => {
    return !!tooth?.materialShade ? tooth?.materialShade : 0;
  }, [tooth?.materialShade]);
  const indicationFormat: IIndicationFormat[] = indicationFormatData?.indication;
  const indicationInfo: IIndicationInfo = indicationInfoData;
  const indicationPartList: (IIndicationCard | undefined)[][] | undefined = useMemo(() => {
    if (!indicationFormat || !indicationInfo) return;

    return indicationFormat
      ?.map((indication: IIndicationFormat) => {
        return indication.list
          ?.map((indicationGroup: IIndicationGroup) => {
            if (!isTypeMore && !indicationGroup.defaultShow) return;

            const indicationInfoPartList: IPartInfo[] = indicationInfo?.partList;
            const partList = indicationInfoPartList.find(
              (partInfo: IPartInfo) => partInfo.alias === indicationGroup.alias,
            );

            return {
              key: partList?.idx || '',
              label:
                partList?.alias.substring(0, 2) !== 'CI'
                  ? t(`INDICATION_${partList?.alias}`)
                  : partList?.name,
              typeColor: partList?.color || '',
              data: {
                groupSeq: indication.seq,
                partSeq: indicationGroup.seq,
                partAlias: indicationGroup.alias,
                color: partList?.color || '',
              },
            };
          })
          .filter(group => !!group);
      })
      .filter(indication => !!indication && indication.length > 0);
  }, [indicationFormat, indicationInfo, isTypeMore]);

  const indicationMaterialList: (IIndicationCard | undefined)[] | undefined = useMemo(() => {
    if (!indicationFormat || !indicationInfo || !indicationIdx) return;
    let materialList: (IIndicationCard | undefined)[] = [];

    indicationFormat?.map((indication: IIndicationFormat) => {
      const indicationFormatMaterialList = indication.list?.find(
        (indicationGroup: IIndicationGroup) => indicationGroup.seq === indicationIdx,
      )?.materialList;
      const indicationInfoMaterialList: IMaterialInfo[] = indicationInfo?.materialList;

      if (!!indicationFormatMaterialList) {
        materialList = indicationFormatMaterialList.map((materialFormat: IMaterialFormat) => {
          const material = indicationInfoMaterialList.find(
            (materialInfo: IMaterialInfo) => materialInfo.alias === materialFormat.alias,
          );
          const materialImg = materialImgList.find(img => img.id === material?.idx);

          return {
            key: material?.idx || '',
            label: material?.name || '',
            materialImg: materialImg?.src,
            data: {
              alias: material?.alias || '',
              idx: material?.idx || '',
            },
          };
        });
      }
    });

    return materialList;
  }, [indicationFormat, indicationInfo, indicationIdx]);

  const indicationShadeList: (IIndicationCard | undefined)[] | undefined = useMemo(() => {
    if (!indicationInfo?.materialShadeList) return;

    return indicationInfo?.materialShadeList.reduce(
      (acc?: IIndicationCard[], curr?: IToothShadeInfo) => {
        const shade = curr?.list.map((shadeInfo: ISeqName) => {
          return { key: shadeInfo.seq, label: shadeInfo.name };
        });
        return [...(acc || []), ...(shade || [])] as IIndicationCard[] | undefined;
      },
      [],
    );
  }, [indicationInfo?.materialShadeList]);

  const isSelectedTooth = useMemo(() => {
    return !!tooth?.number;
  }, [!!tooth?.number]);

  const tabList = useMemo(() => {
    return isPhotogrammetry
      ? [
          { index: 1, label: t(`PROJECT_MATERIAL`) },
          { index: 2, label: t(`PROJECT_TAB_SHADE`) },
        ]
      : [
          { index: 0, label: t(`PROJECT_TAB_TYPE`) },
          { index: 1, label: t(`PROJECT_MATERIAL`) },
          { index: 2, label: t(`PROJECT_TAB_SHADE`) },
        ];
  }, [isPhotogrammetry]);

  // SECTION: declare state
  useEffect(() => {
    if (loadedSource === 'server') {
      UtilActions.fetch_teeth_indication_format_request({
        language: 'EN',
        groupCode: groupCode || '',
      });
      UtilActions.fetch_teeth_indication_info_request({
        groupCode: groupCode || '',
      });
      UtilActions.fetch_teeth_implant_library_request();
    }
    if (loadedSource === 'local') {
      UtilActions.fetch_teeth_indication_format_request({
        channel: 'getIndicationFormatList',
        data: {},
      });
      UtilActions.fetch_teeth_indication_info_request({
        channel: 'getIndicationInfoList',
        data: {},
      });
      UtilActions.fetch_teeth_implant_library_request({
        channel: 'getImplantLibrary',
        data: {},
      });
    }
  }, []);

  useEffect(() => {
    let payload = {};

    if (indicationAlias === '') return;

    if (indicationAlias === 'FDT') {
      if (!!materialAlias) {
        if (loadedSource === 'server') {
          payload = {
            indicationAlias: indicationAlias,
            materialAlias: materialAlias,
            clientGroupCode: groupCode || '',
          };
        }

        if (loadedSource === 'local') {
          payload = {
            channel: 'getIndicationOption',
            data: {
              indicationAlias: indicationAlias,
              materialAlias: materialAlias,
            },
          };
        }
      } else {
        return;
      }
    } else {
      if (loadedSource === 'server') {
        payload = {
          indicationAlias: indicationAlias,
          clientGroupCode: groupCode || '',
        };
      }

      if (loadedSource === 'local') {
        payload = {
          channel: 'getIndicationOption',
          data: {
            indicationAlias: indicationAlias,
          },
        };
      }
    }

    UtilActions.fetch_teeth_indication_option_request(payload);
  }, [indicationAlias, materialAlias]);

  useEffect(() => {
    if (tooth?.hasData && !isPhotogrammetry) {
      setTeeth(overlappingArrayElements({ list: teeth, value: tooth, condition: 'number' }));
    }
  }, [tooth, isPhotogrammetry]);

  // useEffect(() => {
  //   if (!isPhotogrammetry) return;

  //   setIndicationTab(isPhotogrammetry ? 1 : 0);

  //   const defaultPartData = indicationPartList?.[0][0];
  //   if (defaultPartData) {
  //     handleIndicationChange('1', defaultPartData.data, true);
  //   }
  // }, [isPhotogrammetry, indicationPartList]);

  // SECTION: declare function
  const handleMoreChange = () => {
    setIsTypeMore(!isTypeMore);
  };

  const handleTabChange = (index: number) => {
    setIndicationTab(index);
  };

  const handleIndicationChange = (
    index: string,
    data?: IIndicationData,
    isPhotogrammetry?: boolean,
  ) => {
    setTooth((state: ITooth | null) => ({ ...state, indicationIdx: index }));
    setIndicationTab(1);
    setIndicationAlias(data?.partAlias || '');
    setIsHasData(false);

    const isFirstCreated = teeth.find(toothData => toothData.number === tooth?.number);

    // 인디케이션 선택시 재료 자동선택
    const indicationGroup = indicationFormat.find(
      indicationGroup => indicationGroup.seq === data?.groupSeq,
    )?.list;
    const material = indicationGroup?.find(indication => indication.seq === index)?.materialList[0];
    setMaterialAlias(material?.alias || '');

    // 최초 인디케이션 설정한경우 대합치 추가 : photogrametry일때는 제외
    if (!isFirstCreated && !!tooth?.number && !isPhotogrammetry) {
      const isExistTooth = teeth.find(
        toothData => toothData.number === antagonistMapper[tooth?.number],
      );

      // 대합치 설정하는 자리에 이미 치아가 있다면 생성하지 않음
      if (!isExistTooth) {
        const newTooth = {
          number: antagonistMapper[tooth?.number],
          indicationIdx: '27',
          material: '22',
          implantType: 7,
          implantAbutmentMaterialIdx: null,
          implantScanbodyScanIdx: null,
          preparationType: 7,
          reconstructionType: '27',
          materialShade: 0,
          isLast: true,
          description: '',
          default: null,
          advanced: null,
          hasData: true,
          color: '#FCBA3C',
          indicationAlias: 'ATG',
        };

        setTeeth(prev => [...prev, newTooth]);
      }
    }

    const applyToothData = {
      number: tooth?.number,
      indicationIdx: data?.partSeq,
      material: material?.seq || '',
      materialAlias: material?.alias || '',
      implantType: 7,
      implantAbutmentMaterialIdx: null,
      implantScanbodyScanIdx: null,
      preparationType: data?.groupSeq,
      reconstructionType: data?.partSeq,
      materialShade: 0,
      isLast: true,
      description: '',
      default: null,
      advanced: null,
      hasData: true,
      color: data?.color,
      indicationAlias: data?.partAlias || '',
    };

    setTooth({ ...applyToothData });
  };

  const handleMaterialChange = (index: string, data?: IMaterialData) => {
    setMaterialAlias(data?.alias || '');
    setTooth((state: ITooth | null) => ({
      ...state,
      material: index,
      materialAlias: data?.alias || '',
    }));
    setIndicationTab(2);

    if (isPhotogrammetry) {
      setTeeth(prev =>
        prev.map(item => {
          return { ...item, material: index, materialAlias: data?.alias || '' };
        }),
      );
    }
  };

  const handleShadeChange = (index: number) => {
    setTooth((state: ITooth | null) => ({ ...state, materialShade: index }));

    if (isPhotogrammetry) {
      setTeeth(prev =>
        prev.map(item => {
          return { ...item, materialShade: index };
        }),
      );
    }
  };

  return {
    isTypeMore,
    indicationTab,
    indicationPartList,
    indicationIdx,
    indicationMaterialList,
    materialIdx,
    indicationShadeList,
    materialShade,
    isSelectedTooth,
    tabList,
    onMoreChange: handleMoreChange,
    onTabChange: handleTabChange,
    onIndicationChange: handleIndicationChange,
    onMaterialChange: handleMaterialChange,
    onShadeChange: handleShadeChange,
  };
};
